import { Fragment, useMemo } from "react"

import { SOS_BOOK_A_CALL_URL } from "~/ui-rtk/constants/sos"
import useAccess from "~/ui-rtk/hooks/access"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { resolveOwnBrandTerm } from "~/ui-rtk/utils/keywords"

import { computeTrendInsight, formatTrendSentence, TrendRow } from "./insights"

const CUBE = "company_mind_share_metrics_monthly"

const TREND_QUERY = {
  // Deliberately `searches`, not the raw share_of_search column: share is
  // derived in computeTrendInsight the same way the dashboard derives it.
  measures: [`${CUBE}.searches`],
  dimensions: [`${CUBE}.brand`],
  timeDimensions: [
    {
      dimension: `${CUBE}.date`,
      granularity: "month" as const,
      dateRange: "from 6 months ago to now",
    },
  ],
  filters: [
    {
      member: `${CUBE}.metric`,
      operator: "equals" as const,
      values: ["google_keywords_share_of_search_total"],
    },
  ],
}

/**
 * Free-tier upsell hook on the report (APP-452 / MD-4881 step 4.2): a one-line
 * trend callout — how the user's share and the biggest-moving competitor
 * shifted over the last months — with the book-a-call CTA. Renders nothing
 * for paid companies, while loading, or when the data spans fewer than two
 * months (no trend to tell yet).
 */
export default function SosTrendInsights() {
  const { isSosFree } = useAccess()
  const currentCompany = useCurrentCompany()

  const { resultSet } = useCubeQuery(TREND_QUERY, { skip: !isSosFree })

  const insight = useMemo(() => {
    if (!resultSet) {
      return null
    }
    const rows = resultSet.tablePivot() as TrendRow[]
    const google = currentCompany?.metadata?.keywords?.google
    return computeTrendInsight(
      rows,
      resolveOwnBrandTerm(google?.searchAlias, currentCompany?.name),
      google?.brands ?? [],
    )
  }, [resultSet, currentCompany])

  if (!isSosFree || !insight) {
    return null
  }

  return (
    <div className="mx-4 mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-basic-blue bg-base-100 px-4 py-3 shadow">
      <span aria-hidden="true">📈</span>
      <p className="flex-1 min-w-[16rem] text-sm">
        <span className="font-bold">Trend watch:</span>{" "}
        {formatTrendSentence(insight).map((segment, index) =>
          segment.bold ? (
            <span key={index} className="font-semibold">
              {segment.text}
            </span>
          ) : (
            <Fragment key={index}>{segment.text}</Fragment>
          ),
        )}
      </p>
      <a
        href={SOS_BOOK_A_CALL_URL}
        target="_blank"
        rel="noreferrer"
        className="btn btn-primary btn-sm"
      >
        Book a call
      </a>
    </div>
  )
}
